import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import {
  Plus,
  Building2,
  CheckCircle,
  DollarSign,
  Loader2,
  History,
  ChevronDown,
  ChevronUp,
  Bell,
  Mail,
  FileText,
  Download,
  Edit,
  Wrench,
} from "lucide-react";
import { toast } from "sonner";

const RentalManagement = () => {
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [properties, setProperties] = useState([]);
  const [historyDialog, setHistoryDialog] = useState({ open: false, rental: null });
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandedRentals, setExpandedRentals] = useState({});
  const [runningReminders, setRunningReminders] = useState(false);
  const [reminderLog, setReminderLog] = useState([]);
  const [showReminderLog, setShowReminderLog] = useState(false);
  const [selectedRentals, setSelectedRentals] = useState([]);
  const [sendingReminder, setSendingReminder] = useState(null);
  const [editingRental, setEditingRental] = useState(null);
  const [formData, setFormData] = useState({
    property_id: "",
    landlord_name: "",
    landlord_phone: "",
    landlord_email: "",
    landlord_account_number: "",
    landlord_bank_name: "",
    tenant_name: "",
    tenant_phone: "",
    tenant_email: "",
    rental_amount: "",
    management_fee_percent: "10",
    late_penalty_percent: "10",
    lease_start_date: "",
    lease_end_date: "",
    tenant_payment_due_day: "1",
    landlord_payment_due_day: "5",
    also_for_sale: false
  });

  useEffect(() => {
    fetchRentals();
    fetchRentedProperties();
  }, []);

  const fetchRentals = async () => {
    try {
      const response = await axios.get(`${API}/rentals`, { withCredentials: true });
      setRentals(response.data);
    } catch (error) {
      toast.error("Failed to fetch rentals");
    } finally {
      setLoading(false);
    }
  };

  const fetchRentedProperties = async () => {
    try {
      const response = await axios.get(`${API}/properties`, { withCredentials: true });
      setProperties(Array.isArray(response.data) ? response.data : response.data.properties || []);
    } catch (error) {
      console.error("Failed to fetch properties");
      setProperties([]);
    }
  };

  const fetchPaymentHistory = async (rental) => {
    setHistoryDialog({ open: true, rental });
    setHistoryLoading(true);
    setHistoryData(null);
    try {
      const response = await axios.get(`${API}/rentals/${rental.rental_id}/history`, { withCredentials: true });
      setHistoryData(response.data);
    } catch (error) {
      toast.error("Failed to fetch payment history");
    } finally {
      setHistoryLoading(false);
    }
  };

  const runReminders = async () => {
    setRunningReminders(true);
    try {
      const response = await axios.post(`${API}/rentals/run-reminders`, {}, { withCredentials: true });
      const r = response.data.results;
      toast.success(`Reminders processed: ${r.tenant_reminders} tenant, ${r.late_alerts} late alerts, ${r.payout_reminders} payout reminders`);
      fetchReminderLog();
    } catch (error) {
      toast.error("Failed to run reminders");
    } finally {
      setRunningReminders(false);
    }
  };

  const sendSelectedReminders = async () => {
    if (selectedRentals.length === 0) return;
    setRunningReminders(true);
    try {
      const response = await axios.post(`${API}/rentals/send-reminder`, { rental_ids: selectedRentals }, { withCredentials: true });
      const r = response.data.results;
      toast.success(`Sent: ${r.sent}, Skipped: ${r.skipped}, Errors: ${r.errors}`);
      if (r.details) {
        r.details.forEach((d) => {
          if (d.status === "already_paid") toast.info(`${d.tenant}: already paid`);
          if (d.status === "no_email") toast.warning(`${d.tenant}: no email`);
        });
      }
      setSelectedRentals([]);
      fetchReminderLog();
    } catch (error) {
      toast.error("Failed to send reminders");
    } finally {
      setRunningReminders(false);
    }
  };

  const sendSingleReminder = async (rentalId) => {
    setSendingReminder(rentalId);
    try {
      const response = await axios.post(`${API}/rentals/send-reminder`, { rental_ids: [rentalId] }, { withCredentials: true });
      const r = response.data.results;
      if (r.sent > 0) toast.success("Reminder sent");
      else if (r.details?.[0]?.status === "already_paid") toast.info("Tenant already paid this month");
      else if (r.details?.[0]?.status === "no_email") toast.warning("No tenant email on record");
      else toast.error("Failed to send reminder");
      fetchReminderLog();
    } catch (error) {
      toast.error("Failed to send reminder");
    } finally {
      setSendingReminder(null);
    }
  };

  const toggleSelectRental = (rentalId) => {
    setSelectedRentals((prev) =>
      prev.includes(rentalId) ? prev.filter((id) => id !== rentalId) : [...prev, rentalId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedRentals.length === rentals.length) {
      setSelectedRentals([]);
    } else {
      setSelectedRentals(rentals.map((r) => r.rental_id));
    }
  };

  const syncRentalStatus = async () => {
    try {
      const response = await axios.post(`${API}/rentals/sync-status`, {}, { withCredentials: true });
      toast.success(response.data.message);
      if (response.data.fixed_properties?.length > 0) {
        toast.info(`Fixed: ${response.data.fixed_properties.join(", ")}`);
      }
    } catch (error) {
      toast.error("Failed to sync rental status");
    }
  };

  const fetchReminderLog = async () => {
    try {
      const response = await axios.get(`${API}/rentals/reminder-log`, { withCredentials: true });
      setReminderLog(response.data || []);
    } catch (error) {
      console.error("Failed to fetch reminder log");
    }
  };

  const downloadStatement = async (rentalId, propertyRef) => {
    try {
      const response = await axios.get(`${API}/rentals/${rentalId}/statement`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Statement_${propertyRef || rentalId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Statement downloaded");
    } catch (error) {
      toast.error("Failed to download statement");
    }
  };

  const sendStatementToLandlord = async (rentalId, landlordName) => {
    try {
      const response = await axios.post(`${API}/rentals/${rentalId}/send-statement`, {}, { withCredentials: true });
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send statement");
    }
  };

  const sendMaintenanceLink = async (rentalId, tenantName) => {
    try {
      const response = await axios.post(`${API}/rentals/${rentalId}/send-maintenance-link`, {}, { withCredentials: true });
      toast.success(`Maintenance portal link sent to ${tenantName}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send maintenance link");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      rental_amount: parseFloat(formData.rental_amount),
      management_fee_percent: parseFloat(formData.management_fee_percent),
      late_penalty_percent: parseFloat(formData.late_penalty_percent),
      tenant_payment_due_day: parseInt(formData.tenant_payment_due_day),
      landlord_payment_due_day: parseInt(formData.landlord_payment_due_day)
    };
    try {
      if (editingRental) {
        await axios.put(`${API}/rentals/${editingRental.rental_id}`, payload, { withCredentials: true });
        toast.success("Rental record updated");
      } else {
        await axios.post(`${API}/rentals`, payload, { withCredentials: true });
        toast.success("Rental record created");
      }
      setDialogOpen(false);
      setEditingRental(null);
      fetchRentals();
      resetForm();
    } catch (error) {
      toast.error(editingRental ? "Failed to update rental" : "Failed to create rental record");
    }
  };

  const handleEdit = (rental) => {
    setEditingRental(rental);
    setFormData({
      property_id: rental.property_id || "",
      landlord_name: rental.landlord_name || "",
      landlord_phone: rental.landlord_phone || "",
      landlord_email: rental.landlord_email || "",
      landlord_account_number: rental.landlord_account_number || "",
      landlord_bank_name: rental.landlord_bank_name || "",
      tenant_name: rental.tenant_name || "",
      tenant_phone: rental.tenant_phone || "",
      tenant_email: rental.tenant_email || "",
      rental_amount: rental.rental_amount?.toString() || "",
      management_fee_percent: rental.management_fee_percent?.toString() || "10",
      late_penalty_percent: rental.late_penalty_percent?.toString() || "10",
      lease_start_date: rental.lease_start_date || "",
      lease_end_date: rental.lease_end_date || "",
      tenant_payment_due_day: rental.tenant_payment_due_day?.toString() || "1",
      landlord_payment_due_day: rental.landlord_payment_due_day?.toString() || "5"
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      property_id: "", landlord_name: "", landlord_phone: "", landlord_email: "",
      landlord_account_number: "", landlord_bank_name: "", tenant_name: "",
      tenant_phone: "", tenant_email: "", rental_amount: "",
      management_fee_percent: "10", late_penalty_percent: "10",
      lease_start_date: "", lease_end_date: "",
      tenant_payment_due_day: "1", landlord_payment_due_day: "5"
    });
    setEditingRental(null);
  };

  const recordTenantPayment = async (rentalId) => {
    try {
      const response = await axios.post(`${API}/rentals/${rentalId}/payment`, {}, { withCredentials: true });
      toast.success(response.data.message);
      fetchRentals();
    } catch (error) {
      toast.error("Failed to record payment");
    }
  };

  const recordLandlordPayout = async (rentalId) => {
    try {
      const response = await axios.post(`${API}/rentals/${rentalId}/payout`, {}, { withCredentials: true });
      toast.success(response.data.message);
      fetchRentals();
    } catch (error) {
      toast.error("Failed to record payout");
    }
  };

  const toggleExpand = (rentalId) => {
    setExpandedRentals(prev => ({ ...prev, [rentalId]: !prev[rentalId] }));
  };

  const formatMonth = (monthStr) => {
    const [year, month] = monthStr.split("-");
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  };

  if (loading) {
    return <div className="flex justify-center p-8"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-2xl font-serif">Rental Management</h2>
          <p className="text-muted-foreground text-sm">Manage rented properties, payments, and landlord payouts</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={syncRentalStatus} data-testid="sync-rental-status-btn">
            <CheckCircle className="w-4 h-4 mr-2" /> Sync Status
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-rental-btn"><Plus className="w-4 h-4 mr-2" /> Add Rental Record</Button>
            </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingRental ? "Edit Rental Record" : "New Rental Record"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Property *</label>
                <Select value={formData.property_id} onValueChange={(v) => setFormData({...formData, property_id: v})}>
                  <SelectTrigger data-testid="rental-property-select"><SelectValue placeholder="Select a property" /></SelectTrigger>
                  <SelectContent>
                    {properties.map(p => (
                      <SelectItem key={p.property_id} value={p.property_id}>
                        {p.ref_number || p.property_id} - {p.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <h3 className="font-medium text-sm border-b pb-1">Landlord Details</h3>
                  <Input placeholder="Landlord Name *" required value={formData.landlord_name} onChange={(e) => setFormData({...formData, landlord_name: e.target.value})} data-testid="rental-landlord-name" />
                  <Input placeholder="Phone" value={formData.landlord_phone} onChange={(e) => setFormData({...formData, landlord_phone: e.target.value})} />
                  <Input placeholder="Email" type="email" value={formData.landlord_email} onChange={(e) => setFormData({...formData, landlord_email: e.target.value})} />
                  <Input placeholder="Bank Account Number" value={formData.landlord_account_number} onChange={(e) => setFormData({...formData, landlord_account_number: e.target.value})} />
                  <Input placeholder="Bank Name" value={formData.landlord_bank_name} onChange={(e) => setFormData({...formData, landlord_bank_name: e.target.value})} />
                </div>
                <div className="space-y-4">
                  <h3 className="font-medium text-sm border-b pb-1">Tenant Details</h3>
                  <Input placeholder="Tenant Name *" required value={formData.tenant_name} onChange={(e) => setFormData({...formData, tenant_name: e.target.value})} data-testid="rental-tenant-name" />
                  <Input placeholder="Phone" value={formData.tenant_phone} onChange={(e) => setFormData({...formData, tenant_phone: e.target.value})} />
                  <Input placeholder="Email" type="email" value={formData.tenant_email} onChange={(e) => setFormData({...formData, tenant_email: e.target.value})} />
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Rental Amount (K) *</label>
                  <Input type="number" required value={formData.rental_amount} onChange={(e) => setFormData({...formData, rental_amount: e.target.value})} data-testid="rental-amount" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Management Fee %</label>
                  <Input type="number" value={formData.management_fee_percent} onChange={(e) => setFormData({...formData, management_fee_percent: e.target.value})} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Late Penalty %</label>
                  <Input type="number" value={formData.late_penalty_percent} onChange={(e) => setFormData({...formData, late_penalty_percent: e.target.value})} />
                </div>
              </div>

              {/* Fee Preview */}
              {formData.rental_amount && (
                <div className="bg-muted/50 border rounded-sm p-3 text-sm space-y-1">
                  <p className="font-medium text-xs uppercase text-muted-foreground mb-2">Monthly Breakdown Preview</p>
                  <div className="flex justify-between">
                    <span>Rent from tenant</span>
                    <span className="font-mono">K{parseFloat(formData.rental_amount || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-red-600">
                    <span>Management fee ({formData.management_fee_percent}%)</span>
                    <span className="font-mono">-K{(parseFloat(formData.rental_amount || 0) * parseFloat(formData.management_fee_percent || 0) / 100).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between font-semibold border-t pt-1 mt-1">
                    <span>Landlord receives</span>
                    <span className="font-mono text-primary">
                      K{(parseFloat(formData.rental_amount || 0) - (parseFloat(formData.rental_amount || 0) * parseFloat(formData.management_fee_percent || 0) / 100)).toLocaleString()}
                    </span>
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Lease Start Date *</label>
                  <Input type="date" required value={formData.lease_start_date} onChange={(e) => setFormData({...formData, lease_start_date: e.target.value})} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Lease End Date</label>
                  <Input type="date" value={formData.lease_end_date} onChange={(e) => setFormData({...formData, lease_end_date: e.target.value})} />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Tenant Payment Due Day</label>
                  <Input type="number" min="1" max="28" value={formData.tenant_payment_due_day} onChange={(e) => setFormData({...formData, tenant_payment_due_day: e.target.value})} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Landlord Payout Due Day</label>
                  <Input type="number" min="1" max="28" value={formData.landlord_payment_due_day} onChange={(e) => setFormData({...formData, landlord_payment_due_day: e.target.value})} />
                </div>
              </div>

              {/* Also For Sale Option */}
              <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
                <input 
                  type="checkbox" 
                  id="also_for_sale" 
                  checked={formData.also_for_sale}
                  onChange={(e) => setFormData({...formData, also_for_sale: e.target.checked})}
                  className="w-4 h-4 rounded border-gray-300"
                />
                <label htmlFor="also_for_sale" className="text-sm">
                  <span className="font-medium">Also list for sale</span>
                  <span className="text-muted-foreground ml-1">(Property is rented but owner wants to sell)</span>
                </label>
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>Cancel</Button>
                <Button type="submit" data-testid="rental-submit-btn">{editingRental ? "Update Record" : "Create Record"}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Reminder Controls Section */}
      <div className="bg-card border rounded-sm p-4" data-testid="reminder-section">
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Bell className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-medium">Payment Reminders</h3>
                <p className="text-xs text-muted-foreground">
                  Send reminders to all tenants or select specific ones below.
                </p>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap justify-end">
              <Button
                size="sm"
                variant="outline"
                onClick={() => { setShowReminderLog(!showReminderLog); if (!showReminderLog) fetchReminderLog(); }}
                data-testid="toggle-reminder-log"
              >
                <History className="w-4 h-4 mr-1" />
                {showReminderLog ? "Hide Log" : "View Log"}
              </Button>
              {selectedRentals.length > 0 && (
                <Button
                  size="sm"
                  variant="default"
                  onClick={sendSelectedReminders}
                  disabled={runningReminders}
                  data-testid="send-selected-btn"
                >
                  {runningReminders ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />}
                  Send to Selected ({selectedRentals.length})
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={runReminders}
                disabled={runningReminders}
                data-testid="run-reminders-btn"
              >
                {runningReminders ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />}
              {runningReminders ? "Sending..." : "Run Now"}
            </Button>
          </div>
        </div>

        {showReminderLog && (
          <div className="mt-4 border-t pt-4">
            <h4 className="text-sm font-medium mb-2">Recent Reminder Activity</h4>
            {reminderLog.length === 0 ? (
              <p className="text-sm text-muted-foreground">No reminder activity yet</p>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {reminderLog.map((log, i) => (
                  <div key={i} className="text-sm flex items-start gap-2 p-2 bg-muted/50 rounded">
                    {log.type === "scheduler_run" ? (
                      <>
                        <Badge variant="outline" className="text-xs shrink-0">Auto Run</Badge>
                        <div>
                          <p className="text-xs text-muted-foreground">{new Date(log.sent_at).toLocaleString()}</p>
                          <p>
                            Tenant: {log.results?.tenant_reminders || 0},
                            Late: {log.results?.late_alerts || 0},
                            Payout: {log.results?.payout_reminders || 0}
                            {log.results?.errors > 0 && <span className="text-red-500 ml-1">({log.results.errors} errors)</span>}
                          </p>
                        </div>
                      </>
                    ) : (
                      <>
                        <Badge
                          className={`text-xs shrink-0 ${
                            log.type === "tenant_reminder" ? "bg-blue-500" :
                            log.type === "late_alert" ? "bg-red-500" :
                            "bg-green-500"
                          }`}
                        >
                          {log.type === "tenant_reminder" ? "Reminder" :
                           log.type === "late_alert" ? "Late Alert" :
                           "Payout"}
                        </Badge>
                        <div>
                          <p className="text-xs text-muted-foreground">{new Date(log.sent_at).toLocaleString()}</p>
                          <p>Sent to: {log.recipient} ({log.month})</p>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      </div>

      {rentals.length === 0 ? (
        <div className="text-center py-12 bg-card border rounded-sm">
          <Building2 className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
          <p className="text-muted-foreground">No rental records yet</p>
          <p className="text-sm text-muted-foreground">Add a rental record when a property is rented out</p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Select All */}
          <div className="flex items-center gap-2 px-1">
            <input
              type="checkbox"
              checked={selectedRentals.length === rentals.length && rentals.length > 0}
              onChange={toggleSelectAll}
              className="w-4 h-4 rounded border-gray-300"
              data-testid="select-all-rentals"
            />
            <span className="text-sm text-muted-foreground">
              {selectedRentals.length > 0 ? `${selectedRentals.length} of ${rentals.length} selected` : "Select all"}
            </span>
          </div>

          {rentals.map((rental) => {
            const isExpanded = expandedRentals[rental.rental_id];
            const mgmtFee = rental.management_fee || 0;
            const landlordPayable = rental.landlord_payable || (rental.rental_amount - mgmtFee);

            return (
              <div key={rental.rental_id} className={`bg-card border rounded-sm ${selectedRentals.includes(rental.rental_id) ? 'ring-2 ring-primary/30' : ''}`} data-testid={`rental-card-${rental.rental_id}`}>
                {/* Header */}
                <div className="p-4">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={selectedRentals.includes(rental.rental_id)}
                        onChange={() => toggleSelectRental(rental.rental_id)}
                        className="w-4 h-4 rounded border-gray-300 mt-1 shrink-0"
                        data-testid={`select-rental-${rental.rental_id}`}
                      />
                      <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className="font-mono">{rental.property_ref || "N/A"}</Badge>
                        <h3 className="font-medium">{rental.property_title || "Property"}</h3>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Landlord: <span className="font-medium text-foreground">{rental.landlord_name}</span>
                        {rental.landlord_account_number && (
                          <span className="ml-2">| Acc: {rental.landlord_bank_name} - {rental.landlord_account_number}</span>
                        )}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Tenant: <span className="font-medium text-foreground">{rental.tenant_name}</span>
                      </p>
                    </div>
                    </div>
                    <div className="text-right flex flex-col items-end gap-2">
                      <div>
                        <p className="text-lg font-bold">K{rental.rental_amount?.toLocaleString()}/mo</p>
                        <p className="text-xs text-red-500">Fee: -K{mgmtFee.toLocaleString()} ({rental.management_fee_percent}%)</p>
                        <p className="text-sm font-semibold text-primary">Landlord gets: K{landlordPayable.toLocaleString()}</p>
                      </div>
                      <button
                        onClick={() => sendSingleReminder(rental.rental_id)}
                        disabled={sendingReminder === rental.rental_id}
                        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors px-2 py-1 rounded border border-border hover:border-primary disabled:opacity-50"
                        data-testid={`send-reminder-${rental.rental_id}`}
                      >
                        {sendingReminder === rental.rental_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
                        Send Reminder
                      </button>
                    </div>
                  </div>
                  
                  {/* Monthly Breakdown Bar */}
                  <div className="bg-muted/50 border rounded-sm p-3 mb-3">
                    <p className="text-xs font-medium text-muted-foreground uppercase mb-2">Monthly Breakdown</p>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Tenant Pays</p>
                        <p className="font-mono font-semibold">K{rental.rental_amount?.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Mgt Fee ({rental.management_fee_percent}%)</p>
                        <p className="font-mono font-semibold text-red-600">-K{mgmtFee.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Landlord Receives</p>
                        <p className="font-mono font-semibold text-primary">K{landlordPayable.toLocaleString()}</p>
                      </div>
                    </div>
                  </div>

                  {/* Current Month Status */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-3 bg-muted/50 rounded-sm text-sm">
                    <div>
                      <p className="text-muted-foreground">Tenant Due Day</p>
                      <p className="font-medium">{rental.tenant_payment_due_day || 1}th of month</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Landlord Payout Day</p>
                      <p className="font-medium">{rental.landlord_payment_due_day || 5}th of month</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Tenant Status</p>
                      {rental.tenant_paid ? (
                        <Badge className="bg-green-500">Paid</Badge>
                      ) : rental.tenant_late ? (
                        <Badge variant="destructive">Late (+K{rental.late_penalty?.toLocaleString()} penalty)</Badge>
                      ) : (
                        <Badge variant="secondary">Pending</Badge>
                      )}
                    </div>
                    <div>
                      <p className="text-muted-foreground">Landlord Payout</p>
                      {rental.current_payment?.landlord_paid ? (
                        <Badge className="bg-green-500">Paid K{rental.current_payment?.landlord_paid_amount?.toLocaleString()}</Badge>
                      ) : (
                        <Badge variant="secondary">Pending</Badge>
                      )}
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex gap-2 mt-4">
                    {!rental.tenant_paid && (
                      <Button size="sm" onClick={() => recordTenantPayment(rental.rental_id)} data-testid={`mark-tenant-paid-${rental.rental_id}`}>
                        <CheckCircle className="w-4 h-4 mr-1" /> Mark Tenant Paid
                      </Button>
                    )}
                    {rental.tenant_paid && !rental.current_payment?.landlord_paid && (
                      <Button size="sm" variant="outline" onClick={() => recordLandlordPayout(rental.rental_id)} data-testid={`mark-landlord-paid-${rental.rental_id}`}>
                        <DollarSign className="w-4 h-4 mr-1" /> Pay Landlord (K{landlordPayable.toLocaleString()})
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => fetchPaymentHistory(rental)} data-testid={`view-history-${rental.rental_id}`}>
                      <History className="w-4 h-4 mr-1" /> Payment History
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => downloadStatement(rental.rental_id, rental.property_ref)} data-testid={`download-statement-${rental.rental_id}`}>
                      <Download className="w-4 h-4 mr-1" /> Statement
                    </Button>
                    {rental.landlord_email && (
                      <Button size="sm" variant="ghost" onClick={() => sendStatementToLandlord(rental.rental_id, rental.landlord_name)} data-testid={`send-statement-${rental.rental_id}`}>
                        <FileText className="w-4 h-4 mr-1" /> Email Landlord
                      </Button>
                    )}
                    {rental.tenant_email && (
                      <Button size="sm" variant="ghost" onClick={() => sendMaintenanceLink(rental.rental_id, rental.tenant_name)} data-testid={`send-maintenance-${rental.rental_id}`}>
                        <Wrench className="w-4 h-4 mr-1" /> Send Maintenance Link
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => handleEdit(rental)} data-testid={`edit-rental-${rental.rental_id}`}>
                      <Edit className="w-4 h-4 mr-1" /> Edit
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Payment History Dialog */}
      <Dialog open={historyDialog.open} onOpenChange={(open) => setHistoryDialog({ open, rental: open ? historyDialog.rental : null })}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif">Payment History</DialogTitle>
          </DialogHeader>
          {historyLoading ? (
            <div className="flex justify-center p-8"><Loader2 className="w-8 h-8 animate-spin" /></div>
          ) : historyData ? (
            <div className="space-y-6" data-testid="payment-history-content">
              {/* Rental Info */}
              <div className="bg-muted/50 rounded-sm p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="outline" className="font-mono">{historyData.property_ref || "N/A"}</Badge>
                  <span className="font-medium">{historyData.property_title}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <p>Landlord: <span className="font-medium">{historyData.landlord_name}</span></p>
                  <p>Tenant: <span className="font-medium">{historyData.tenant_name}</span></p>
                  <p>Rent: <span className="font-mono font-medium">K{historyData.rental_amount?.toLocaleString()}/mo</span></p>
                  <p>Mgt Fee: <span className="font-medium">{historyData.management_fee_percent}%</span></p>
                </div>
              </div>

              {/* Summary Totals */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-card border rounded-sm p-3 text-center">
                  <p className="text-xs text-muted-foreground">Rent Collected</p>
                  <p className="font-mono font-bold text-lg">K{historyData.totals.rent_collected?.toLocaleString()}</p>
                </div>
                <div className="bg-card border rounded-sm p-3 text-center">
                  <p className="text-xs text-muted-foreground">Fees Earned</p>
                  <p className="font-mono font-bold text-lg text-red-600">K{historyData.totals.management_fees?.toLocaleString()}</p>
                </div>
                <div className="bg-card border rounded-sm p-3 text-center">
                  <p className="text-xs text-muted-foreground">Paid to Landlord</p>
                  <p className="font-mono font-bold text-lg text-primary">K{historyData.totals.landlord_payouts?.toLocaleString()}</p>
                </div>
                <div className="bg-card border rounded-sm p-3 text-center">
                  <p className="text-xs text-muted-foreground">Outstanding</p>
                  <p className={`font-mono font-bold text-lg ${historyData.totals.outstanding_to_landlord > 0 ? 'text-yellow-600' : 'text-green-600'}`}>
                    K{historyData.totals.outstanding_to_landlord?.toLocaleString()}
                  </p>
                </div>
              </div>

              {/* Payment Records */}
              {historyData.payments.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <History className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p>No payment records yet</p>
                </div>
              ) : (
                <div className="border rounded-sm overflow-hidden">
                  <div className="grid grid-cols-12 gap-2 p-3 bg-muted text-xs font-medium">
                    <div className="col-span-2">Month</div>
                    <div className="col-span-2">Rent</div>
                    <div className="col-span-2">Fee</div>
                    <div className="col-span-2">Landlord</div>
                    <div className="col-span-2">Tenant</div>
                    <div className="col-span-2">Payout</div>
                  </div>
                  <div className="divide-y divide-border">
                    {historyData.payments.map((payment) => (
                      <div key={payment.payment_month} className="grid grid-cols-12 gap-2 p-3 text-sm items-center hover:bg-muted/50">
                        <div className="col-span-2 font-medium">{formatMonth(payment.payment_month)}</div>
                        <div className="col-span-2 font-mono">K{(payment.rental_amount || payment.tenant_paid_amount || 0).toLocaleString()}</div>
                        <div className="col-span-2 font-mono text-red-600">
                          -K{(payment.management_fee || 0).toLocaleString()}
                        </div>
                        <div className="col-span-2 font-mono text-primary">
                          K{(payment.landlord_payable || (payment.rental_amount || 0) - (payment.management_fee || 0)).toLocaleString()}
                        </div>
                        <div className="col-span-2">
                          {payment.tenant_paid ? (
                            <Badge className="bg-green-500 text-xs">Paid {payment.tenant_paid_date}</Badge>
                          ) : (
                            <Badge variant="secondary" className="text-xs">Unpaid</Badge>
                          )}
                          {payment.tenant_late && payment.late_penalty_amount > 0 && (
                            <p className="text-xs text-red-500 mt-0.5">+K{payment.late_penalty_amount?.toLocaleString()} late</p>
                          )}
                        </div>
                        <div className="col-span-2">
                          {payment.landlord_paid ? (
                            <Badge className="bg-green-500 text-xs">K{payment.landlord_paid_amount?.toLocaleString()}</Badge>
                          ) : payment.tenant_paid ? (
                            <Badge variant="outline" className="text-xs text-yellow-600">Pending</Badge>
                          ) : (
                            <Badge variant="secondary" className="text-xs">-</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RentalManagement;
